import { useState } from "react";
import { Button } from "./ui/button";
import { cn } from "../../../lib/utils";
import { ArrowRight, User, Search, FileText, Check, X } from "lucide-react";
import { useNavigate } from "react-router-dom"; // 1. Import useNavigate
import { pricingPlans } from "../../../data/lovePlans"; // 2. Import plans data
import { trackReportEvent } from "../../../utils/analytics";

const PricingSection = () => {
  const navigate = useNavigate();

  const steps = [
    {
      title: "Tell Us About You:",
      description: "Just enter your birth details (date, time, place)"
    },
    {
      title: "We Study Your Chart:",
      description: "Our expert astrologers look at your love traits"
    },
    {
      title: "Get Your Report:",
      description: "A custom love report created just for you"
    }
  ];

  const handleBuyNow = (plan) => {
    trackReportEvent("select", {
      reportName: plan.title,
      reportId: plan.id,
      price: plan.price,
    });

    navigate(`/love-report/${plan.id}`);
  };

  const handleScrollToPricing = () => {
    const element = document.getElementById("pricing-section");
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <section className="py-20 bg-background_lv">
      <div className="container mx-auto px-4">
        {/* How It Works Section */}
        <div className="text-center mb-12 lg:mb-20">
          <h2 className="text-3xl lg:text-4xl xl:text-5xl font-bold mb-2">
            How It Works{" "}
            <span className="text-love-pink">(It's Super Simple!)</span>
          </h2>
          <div className="w-24 lg:w-32 h-1 bg-love-pink mx-auto mb-8 lg:mb-16"></div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8 max-w-5xl mx-auto">
            {steps.map((step, index) => (
              <div key={index} className="flex flex-col md:flex-row items-center justify-center">
                <div className="text-center">
                  <h3 className="text-lg lg:text-xl font-bold text-love-pink mb-2">
                    {step.title}
                  </h3>
                  <p className="text-muted_lv-foreground text-sm lg:text-base">
                    {step.description}
                  </p>
                </div>
                {index < steps.length - 1 && (
                  <ArrowRight className="hidden md:block h-6 w-6 text-love-pink mx-4 flex-shrink-0" />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Pricing Section */}
        <div id="pricing-section" className="text-center mb-8 lg:mb-16">
          <h2 className="text-3xl lg:text-4xl xl:text-5xl font-bold mb-4">
            Choose The Love Report{" "}
            <span className="text-love-pink">That's Right For You</span>
          </h2>
          <p className="text-lg lg:text-xl text-muted_lv-foreground">
            Start small or go deep → your options are clear!
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8 max-w-7xl mx-auto mb-8 lg:mb-16">
          {pricingPlans.map((plan, index) => (
            <div
              key={index}
              className={`bg-love-pink text-love-pink-foreground rounded-2xl p-8 relative ${plan.badge === "BEST SELLER" ? "transform scale-105 border-4 border-love-pink/50" : ""
                }`}
            >
              {/* Badge */}
              <div className="text-center mb-4">
                <span className="bg-white/20 px-4 py-1 rounded-full text-sm font-bold">
                  {plan.badge}
                </span>
              </div>

              {/* Title */}
              <h3 className="text-2xl font-bold text-center mb-6">
                {plan.title}
              </h3>

              {/* Price */}
              <div className="text-center mb-8">
                <div className="text-4xl font-bold">{plan.priceFinal}</div>
                <div className="text-lg line-through opacity-75">{plan.priceOriginal}</div>
              </div>

              {/* Includes */}
              <div className="mb-8">
                <h4 className="font-bold mb-4 text-center">Includes:</h4>
                <ul className="space-y-3">
                  {plan.features.map((item, itemIndex) => (
                    <li key={itemIndex} className="flex items-start text-sm">
                      <Check className="h-4 w-4 mt-0.5 mr-2 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Button */}

              <Button
                variant="secondary"
                size="lg"
                className="w-full border border-white"
                onClick={() => handleBuyNow(plan)}
              >
                Order Now
              </Button>

            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="bg-love-pink text-love-pink-foreground rounded-3xl p-12 text-center relative overflow-hidden">
          <div className="relative z-10">
            <h3 className="text-3xl lg:text-4xl font-bold mb-4">
              Let's Explore Your Love Story,<br />
              Get Your Love Report Today!
            </h3>
            <Button
              variant="secondary"
              size="lg"
              className="text-lg px-8 py-4"
              onClick={handleScrollToPricing}
            >
              Order Now
            </Button>
          </div>
          {/* Decorative hearts */}
          <div className="absolute top-4 left-4 opacity-20">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
            </svg>
          </div>
          <div className="absolute bottom-4 right-4 opacity-20">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
            </svg>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PricingSection;